import React from 'react';
import { Heart, MessageCircle, Send, Bookmark, Music, MoreHorizontal } from 'lucide-react';
import { MediaDisplay } from './MediaDisplay';
import type { PreviewProps } from './types';

export const InstagramPreview: React.FC<PreviewProps> = ({ content, brand, mediaUrl, format = 'regular' }) => {
  // Regular Instagram post
  if (format === 'regular') {
    return (
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden max-w-sm mx-auto">
        {/* Header */}
        <div className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gray-100 overflow-hidden">
                {brand?.logo ? (
                  <img src={brand.logo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-200" />
                )}
              </div>
              <span className="font-semibold text-sm">
                {brand?.name || 'company_name'}
              </span>
            </div>
            <button className="p-1">
              <MoreHorizontal className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Media (primary element for Instagram) */}
        <div className="relative w-full pb-[125%] bg-gray-100 flex items-center justify-center">
          <MediaDisplay
            mediaUrl={mediaUrl}
            altText="Instagram post"
            type="image"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        {/* Actions */}
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <button>
                <Heart className="w-6 h-6" />
              </button>
              <button>
                <MessageCircle className="w-6 h-6" />
              </button>
              <button>
                <Send className="w-6 h-6" />
              </button>
            </div>
            <button>
              <Bookmark className="w-6 h-6" />
            </button>
          </div>

          {/* Likes */}
          <div className="mb-1">
            <p className="text-sm font-semibold">3,241 likes</p>
          </div>

          {/* Caption */}
          <div className="mb-1">
            <p className="whitespace-pre-line" style={{ fontSize: '0.8rem' }}>
              <span className="font-semibold mr-1">{brand?.name || 'company_name'}</span>
              {content}
            </p>
          </div>

          {/* Comments preview */}
          <p className="text-sm text-gray-500">View all 42 comments</p>

          {/* Time */}
          <p className="text-xs text-gray-400 mt-1">2 HOURS AGO</p>
        </div>
      </div>
    );
  }
  
  // Instagram Reels preview
  return (
    <div className="bg-black text-white rounded-xl shadow-md border border-gray-800 overflow-hidden max-w-[320px] mx-auto">
      <div className="relative w-full aspect-[9/16] bg-gray-900 flex flex-col">
        {/* Main content */}
        <div className="flex-1 relative">
          <MediaDisplay
            mediaUrl={mediaUrl}
            altText="Instagram Reel"
            type="video"
            aspectRatio="9/16"
          />
          
          {/* Right side actions */}
          <div className="absolute right-3 bottom-24 flex flex-col items-center space-y-5">
            <button className="flex flex-col items-center">
              <Heart className="w-7 h-7" />
              <span className="text-xs mt-1">142k</span>
            </button>
            <button className="flex flex-col items-center">
              <MessageCircle className="w-7 h-7" />
              <span className="text-xs mt-1">1,024</span>
            </button>
            <button className="flex flex-col items-center">
              <Send className="w-7 h-7" />
              <span className="text-xs mt-1">483</span>
            </button>
            <button className="flex flex-col items-center">
              <Bookmark className="w-7 h-7" />
            </button>
            <button className="flex flex-col items-center">
              <MoreHorizontal className="w-7 h-7" />
            </button>
          </div>

          {/* Bottom caption */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-8 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
                {brand?.logo ? (
                  <img src={brand.logo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-200" />
                )}
              </div>
              <span className="font-semibold text-sm">{brand?.name || 'company_name'}</span>
              <button className="border border-white text-white text-xs px-2 py-1 rounded">
                Follow
              </button>
            </div>
            <p className="mb-3 whitespace-pre-line" style={{ fontSize: '0.8rem' }}>{content}</p>
            <div className="flex items-center">
              <Music className="w-3 h-3 mr-2" />
              <div className="flex-1 text-xs truncate">
                Original audio • {brand?.name || 'company_name'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};